import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  Upload, Camera, Sparkles, Loader2, Download, RefreshCw, ChefHat, Shirt, DoorOpen,
  Palette, CheckCircle, ArrowRight, Image as ImageIcon, X, Wand2, Share2, MessageCircle,
  Sun, Moon, LayoutGrid, TrendingUp, FileText, GitCompare, Info, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  FINISHES, TEXTURES, STYLES, HANDLE_PROFILES, HANDLE_COLORS,
  WARDROBE_TYPES, WARDROBE_DOOR_DESIGNS, LIGHTINGS, generateBrandedDesignPDF,
} from '../lib/designStudio';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import ARDoorPreview from '../components/ARDoorPreview';
import KenBurnsExport from '../components/KenBurnsExport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIVisualizer = () => {
  const navigate = useNavigate();
  const [selectedRoom, setSelectedRoom] = useState('kitchen');

  // Core options
  const [selectedFinish, setSelectedFinish] = useState('high-gloss-white');
  const [selectedFinishLower, setSelectedFinishLower] = useState('wood-walnut');
  const [selectedTexture, setSelectedTexture] = useState('high-gloss');
  const [selectedStyle, setSelectedStyle] = useState('modern');
  const [twoTone, setTwoTone] = useState(false);
  const [lighting, setLighting] = useState('day');

  // Kitchen-only
  const [handleProfile, setHandleProfile] = useState('handleless-j');
  const [handleColor, setHandleColor] = useState('chrome');

  // Wardrobe-only
  const [wardrobeType, setWardrobeType] = useState('openable');
  const [wardrobeDoorDesign, setWardrobeDoorDesign] = useState('plain');

  // Comparison mode (retain existing feature)
  const [compareMode, setCompareMode] = useState(false);
  const [selectedFinishB, setSelectedFinishB] = useState('matte-grey');
  const [comparisonResult, setComparisonResult] = useState(null);

  // Photo / preset
  const [uploadedImage, setUploadedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [presets, setPresets] = useState([]);
  const [popularCombos, setPopularCombos] = useState([]);

  // Result
  const [generatedImage, setGeneratedImage] = useState(null);
  const [shareId, setShareId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);

  // Tier 2/3
  const [arActive, setArActive] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [showBeforeAfter, setShowBeforeAfter] = useState(true);

  const fileInputRef = useRef(null);

  const loadingMessages = [
    'Analyzing your room photo...',
    'Applying CriLam PUR-bonded finish...',
    `Rendering ${TEXTURES.find((t) => t.id === selectedTexture)?.label || 'premium'} texture...`,
    'Simulating realistic lighting and shadows...',
    'Adding final designer touches...',
  ];

  useEffect(() => {
    if (!isGenerating) { setLoadingMsgIndex(0); return; }
    const interval = setInterval(() => setLoadingMsgIndex((prev) => (prev + 1) % loadingMessages.length), 7000);
    return () => clearInterval(interval);
  }, [isGenerating, loadingMessages.length]);

  // Route Doors selection to dedicated designer
  const roomTypes = [
    { id: 'kitchen', label: 'Kitchen', icon: ChefHat },
    { id: 'wardrobe', label: 'Wardrobe', icon: Shirt },
    { id: 'door', label: 'Doors', icon: DoorOpen, external: true },
  ];

  // Load presets and popular combos when room changes
  useEffect(() => {
    (async () => {
      try {
        const [pres, combos] = await Promise.all([
          fetch(`${API_URL}/api/room-presets/${selectedRoom}`).then((r) => r.json()).catch(() => ({ presets: [] })),
          fetch(`${API_URL}/api/popular-combos/${selectedRoom}`).then((r) => r.json()).catch(() => ({ combos: [] })),
        ]);
        setPresets(pres.presets || []);
        setPopularCombos(combos.combos || []);
      } catch {
        setPresets([]); setPopularCombos([]);
      }
    })();
  }, [selectedRoom]);

  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Image size should be less than 10MB'); return; }
    setUploadedImage(file);
    setPreviewUrl(URL.createObjectURL(file));
    setSelectedPreset(null);
    setGeneratedImage(null);
    setError(null);
    setStep(2);
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 10 * 1024 * 1024) { setError('Image size should be less than 10MB'); return; }
      setUploadedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setSelectedPreset(null);
      setGeneratedImage(null);
      setError(null);
      setStep(2);
    }
  }, []);

  const pickPreset = (preset) => {
    setSelectedPreset(preset);
    setUploadedImage(null);
    setPreviewUrl(preset.image);
    setStep(2);
  };

  const applyPopularCombo = (combo) => {
    setSelectedFinish(combo.finish_upper);
    if (combo.finish_lower) { setSelectedFinishLower(combo.finish_lower); setTwoTone(true); } else { setTwoTone(false); }
    if (combo.texture) setSelectedTexture(combo.texture);
    if (combo.handle_profile) setHandleProfile(combo.handle_profile);
    if (combo.handle_color) setHandleColor(combo.handle_color);
    if (combo.wardrobe_type) setWardrobeType(combo.wardrobe_type);
    if (combo.wardrobe_door_design) setWardrobeDoorDesign(combo.wardrobe_door_design);
    toast.success(`Applied "${combo.name}" combo`);
  };

  const buildVisualizePayload = (finishA, forCompare = false) => ({
    image: !selectedPreset ? (previewUrl?.startsWith('data:') ? previewUrl : null) : null,
    preset_id: selectedPreset?.id || null,
    room_type: selectedRoom,
    finish: finishA,
    finish_lower: (!forCompare && twoTone) ? selectedFinishLower : null,
    style: selectedStyle,
    texture: selectedTexture,
    two_tone: !forCompare && twoTone,
    handle_profile: selectedRoom === 'kitchen' ? handleProfile : null,
    handle_color: selectedRoom === 'kitchen' ? handleColor : null,
    wardrobe_type: selectedRoom === 'wardrobe' ? wardrobeType : null,
    wardrobe_door_design: selectedRoom === 'wardrobe' ? wardrobeDoorDesign : null,
    lighting,
  });

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const callVisualize = async (finishA, base64Image = null, forCompare = false) => {
    const useUploadedPhoto = !!uploadedImage && !selectedPreset;
    const url = useUploadedPhoto ? `${API_URL}/api/visualize` : `${API_URL}/api/preset-visualize`;
    const payload = buildVisualizePayload(finishA, forCompare);
    if (useUploadedPhoto) payload.image = base64Image;
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to generate visualization');
    }
    const d = await r.json();
    if (!d.success || !d.image_url) throw new Error('No image returned from server');
    return { url: d.image_url, shareId: d.share_id || null, finish: finishA };
  };

  const generateVisualization = async () => {
    if (!uploadedImage && !selectedPreset) { setError('Please upload a photo or pick a room preset first'); return; }
    if (compareMode && selectedFinish === selectedFinishB) { setError('Please choose two different finishes to compare'); return; }
    setIsGenerating(true);
    setError(null);
    try {
      const base64 = uploadedImage ? await fileToBase64(uploadedImage) : null;
      if (compareMode) {
        const [a, b] = await Promise.all([
          callVisualize(selectedFinish, base64, true),
          callVisualize(selectedFinishB, base64, true),
        ]);
        setComparisonResult({ a, b });
        setGeneratedImage(null); setShareId(null);
      } else {
        const r = await callVisualize(selectedFinish, base64, false);
        setGeneratedImage(r.url); setShareId(r.shareId); setComparisonResult(null);
      }
      setStep(3);
    } catch (e) {
      setError(e.message || 'Failed to generate visualization. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const resetVisualizer = () => {
    setUploadedImage(null); setPreviewUrl(null); setSelectedPreset(null);
    setGeneratedImage(null); setShareId(null); setError(null); setComparisonResult(null);
    setStep(1);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const roomIsKitchen = selectedRoom === 'kitchen';
  const roomIsWardrobe = selectedRoom === 'wardrobe';

  const downloadImage = () => {
    if (!generatedImage) return;
    const a = document.createElement('a');
    a.href = generatedImage;
    a.download = `crilam-${selectedRoom}-${selectedFinish}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    toast.success('Image downloaded');
  };

  const downloadCompareImage = (opt) => {
    const item = comparisonResult?.[opt];
    if (!item) return;
    const a = document.createElement('a');
    a.href = item.url;
    a.download = `crilam-${selectedRoom}-${item.finish}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const downloadPDF = async () => {
    if (!generatedImage) return;
    setPdfGenerating(true);
    try {
      const fUpper = FINISHES.find((f) => f.id === selectedFinish);
      const fLower = FINISHES.find((f) => f.id === selectedFinishLower);
      const tex = TEXTURES.find((t) => t.id === selectedTexture);
      const extras = [];
      if (twoTone) extras.push(['Two-Tone:', `Upper: ${fUpper?.label} · Lower: ${fLower?.label}`]);
      if (roomIsKitchen) extras.push(
        ['Handle Profile:', HANDLE_PROFILES.find((h) => h.id === handleProfile)?.label],
        ['Handle Finish:', HANDLE_COLORS.find((h) => h.id === handleColor)?.label],
      );
      if (roomIsWardrobe) extras.push(
        ['Wardrobe Type:', WARDROBE_TYPES.find((w) => w.id === wardrobeType)?.label],
        ['Door Design:', WARDROBE_DOOR_DESIGNS.find((d) => d.id === wardrobeDoorDesign)?.label],
      );
      extras.push(
        ['Style:', STYLES.find((s) => s.id === selectedStyle)?.label],
        ['Lighting:', LIGHTINGS.find((l) => l.id === lighting)?.label],
      );
      const doc = await generateBrandedDesignPDF({
        imageDataUrl: generatedImage,
        title: `CriLam ${roomIsKitchen ? 'Modular Kitchen' : 'Wardrobe'} Design`,
        productLabel: `CriLam 6H Acrylic Boards · ${roomIsKitchen ? 'Modular Kitchen' : 'Wardrobe'} Application`,
        designCode: `${selectedRoom.toUpperCase()}-${selectedFinish.toUpperCase()}`,
        finishLabel: fUpper?.label,
        finishCode: fUpper?.code,
        textureLabel: tex?.label,
        extraSpecs: extras,
      });
      doc.save(`crilam-${selectedRoom}-design.pdf`);
      toast.success('Branded design sheet downloaded');
    } catch {
      toast.error('Could not generate PDF.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const shareToWhatsApp = async () => {
    if (!generatedImage) return;
    const finishLabel = FINISHES.find((f) => f.id === selectedFinish)?.label;
    const shareText = `Check out how my ${selectedRoom} would look with CriLam ${finishLabel} finish! What do you think?`;
    try {
      const res = await fetch(generatedImage);
      const blob = await res.blob();
      const file = new File([blob], 'crilam-visualization.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'My CriLam Visualization', text: shareText });
        return;
      }
    } catch (e) { if (e.name === 'AbortError') return; }
    const link = shareId ? `${API_URL}/api/visualization-image/${shareId}` : '';
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n\nView design: ${link}\n\nDesign yours: ${window.location.origin}/ai-visualizer`)}`, '_blank', 'noopener,noreferrer');
  };

  const shareComparisonToWhatsApp = () => {
    if (!comparisonResult) return;
    const finishA = FINISHES.find((f) => f.id === comparisonResult.a.finish)?.label;
    const finishB = FINISHES.find((f) => f.id === comparisonResult.b.finish)?.label;
    const linkA = comparisonResult.a.shareId ? `${API_URL}/api/visualization-image/${comparisonResult.a.shareId}` : '';
    const linkB = comparisonResult.b.shareId ? `${API_URL}/api/visualization-image/${comparisonResult.b.shareId}` : '';
    const msg = `I can't decide! Which finish looks better for my ${selectedRoom}?\n\nOption A - ${finishA}: ${linkA}\n\nOption B - ${finishB}: ${linkB}\n\nMade with CriLam AI Visualizer: ${window.location.origin}/ai-visualizer`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="ai-visualizer-page">
      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              <Sparkles className="h-4 w-4" /> AI DESIGN STUDIO
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit']">
              Visualize Your Kitchen & Wardrobe
            </h1>
            <p className="text-lg text-[#D4AF37] font-medium mb-3">
              Two-Tone Combos · 5 Signature Textures · Handle Profiles · Wardrobe Designs · Lighting Simulation
            </p>
            <p className="text-white/70 max-w-2xl mx-auto">
              Upload your room photo — or pick from our premium presets — and our AI shows you the exact
              CriLam PUR-bonded finish combination. Powered by real product codes and 10-year guarantee.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Progress */}
      <section className="py-5 bg-white border-b sticky top-0 z-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {[{ n: 1, l: 'Choose Room & Photo' }, { n: 2, l: 'Customize Finish' }, { n: 3, l: 'View Result' }].map((s, i) => (
              <div key={s.n} className="flex items-center">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${step >= s.n ? 'bg-[#004534] text-white' : 'bg-gray-100 text-gray-400'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${step > s.n ? 'bg-[#D4AF37] text-[#004534]' : ''}`}>
                    {step > s.n ? <CheckCircle className="h-4 w-4" /> : s.n}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">{s.l}</span>
                </div>
                {i < 2 && <div className={`w-6 lg:w-12 h-0.5 mx-2 ${step > s.n ? 'bg-[#004534]' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 lg:py-16">
        <div className="container mx-auto px-6 lg:px-12 max-w-6xl">

          {/* Step 1 */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto">
              {/* Room type */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-[#004534] mb-3">What do you want to visualize?</label>
                <div className="flex flex-wrap gap-3">
                  {roomTypes.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => {
                        if (r.external) { navigate('/door-designer'); return; }
                        setSelectedRoom(r.id);
                      }}
                      className={`flex items-center gap-2 px-5 py-3 rounded-lg font-medium transition-all ${
                        selectedRoom === r.id ? 'bg-[#004534] text-white'
                          : 'bg-white text-gray-600 border border-gray-200 hover:border-[#004534]'
                      }`}
                      data-testid={`room-${r.id}`}
                    >
                      <r.icon className="h-5 w-5" /> {r.label}
                      {r.external && <span className="ml-1 text-[10px] bg-[#D4AF37] text-[#004534] px-1.5 py-0.5 rounded-full font-bold">CNC Designer</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Upload */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="border-2 border-dashed border-gray-300 rounded-2xl p-10 text-center bg-white hover:border-[#004534] transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-area"
              >
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" data-testid="file-input" />
                <div className="w-20 h-20 mx-auto mb-4 bg-[#004534]/10 rounded-full flex items-center justify-center">
                  <Upload className="h-10 w-10 text-[#004534]" />
                </div>
                <h3 className="text-xl font-bold text-[#004534] mb-2">Upload Your Room Photo</h3>
                <p className="text-gray-500 mb-2">Drag & drop or click to upload · JPG/PNG/WebP · Max 10MB</p>
              </div>

              {/* Presets fallback */}
              <div className="mt-8 bg-white rounded-2xl p-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <LayoutGrid className="h-5 w-5 text-[#D4AF37]" />
                  <h4 className="font-bold text-[#004534]">No photo? Start from a preset layout</h4>
                </div>
                <p className="text-sm text-gray-500 mb-4">Pick a curated Indian {selectedRoom} layout — we&apos;ll render it in your chosen CriLam finish.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {presets.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => pickPreset(p)}
                      className={`text-left border rounded-lg p-3 transition-all ${
                        selectedPreset?.id === p.id ? 'border-[#004534] bg-[#004534]/5' : 'border-gray-200 hover:border-[#004534] bg-white'
                      }`}
                      data-testid={`preset-${p.id}`}
                    >
                      <div className="aspect-video bg-gradient-to-br from-[#004534]/8 to-[#D4AF37]/15 rounded-md mb-2 flex items-center justify-center">
                        <LayoutGrid className="h-6 w-6 text-[#004534]/40" />
                      </div>
                      <p className="text-sm font-semibold text-[#004534]">{p.name}</p>
                      <p className="text-xs text-gray-500 line-clamp-2">{p.layout}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tips */}
              <div className="mt-6 p-5 bg-[#004534]/5 rounded-xl text-sm text-gray-600">
                <h4 className="font-semibold text-[#004534] mb-2 flex items-center gap-2">
                  <Camera className="h-5 w-5" /> Tips for Best Results
                </h4>
                <ul className="space-y-1 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> Bright daytime photo works best</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> Capture the full area you want redesigned</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> Keep the camera level — avoid tilted angles</li>
                </ul>
              </div>
            </motion.div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-5 gap-6">
              {/* Preview */}
              <div className="lg:col-span-2 space-y-4">
                <div className="relative aspect-video bg-gray-100 rounded-xl overflow-hidden shadow-lg">
                  {previewUrl && !selectedPreset && <img src={previewUrl} alt="Your room" className="w-full h-full object-cover" />}
                  {selectedPreset && (
                    <div className="w-full h-full flex items-center justify-center text-center px-4 bg-gradient-to-br from-[#004534]/15 to-[#D4AF37]/15">
                      <div>
                        <LayoutGrid className="h-8 w-8 text-[#004534] mx-auto mb-2" />
                        <p className="font-semibold text-[#004534]">{selectedPreset.name}</p>
                        <p className="text-xs text-gray-500 mt-1 max-w-xs">{selectedPreset.layout}</p>
                      </div>
                    </div>
                  )}
                  <button onClick={resetVisualizer} className="absolute top-3 right-3 p-2 bg-white/90 rounded-full hover:bg-white" data-testid="reset-photo-btn">
                    <X className="h-4 w-4 text-gray-600" />
                  </button>
                  <div className="absolute bottom-3 left-3 px-3 py-1 bg-black/50 text-white text-xs rounded-full">
                    {selectedPreset ? 'Preset Layout' : 'Your Photo'}
                  </div>
                </div>

                {/* Popular combos */}
                {popularCombos.length > 0 && (
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <h4 className="font-bold text-[#004534] text-sm flex items-center gap-2 mb-1">
                      <TrendingUp className="h-4 w-4 text-[#D4AF37]" /> Popular {roomIsKitchen ? 'Kitchen' : 'Wardrobe'} Combos
                    </h4>
                    <p className="text-xs text-gray-500 mb-3">Loved by CriLam customers this month — one tap to try.</p>
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {popularCombos.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => applyPopularCombo(c)}
                          className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-[#004534] hover:bg-[#004534]/5 transition-all"
                          data-testid={`combo-${c.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-[#004534]">{c.name}</span>
                                <span className="text-[9px] px-1.5 py-0.5 bg-[#D4AF37]/25 text-[#7A6621] rounded font-bold uppercase tracking-wide">{c.tag}</span>
                                {c.live_count > 0 && (
                                  <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-bold uppercase tracking-wide flex items-center gap-0.5" title={`${c.live_count} live selection(s) in last 30 days`}>
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                    LIVE {c.live_count > 1 ? `+${c.live_count}` : ''}
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-gray-500 mt-0.5">
                                {c.sold_this_month}+ chose this combo this month
                              </p>
                            </div>
                            <div className="flex gap-1">
                              <div className="w-5 h-5 rounded-full border border-gray-200" style={{ backgroundColor: FINISHES.find((f) => f.id === c.finish_upper)?.color }} />
                              {c.finish_lower && <div className="w-5 h-5 rounded-full border border-gray-200" style={{ backgroundColor: FINISHES.find((f) => f.id === c.finish_lower)?.color }} />}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Options */}
              <div className="lg:col-span-3 space-y-5">
                {/* Two-Tone */}
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="two-tone-toggle-wrap">
                    <input type="checkbox" checked={twoTone} onChange={(e) => setTwoTone(e.target.checked)} className="w-5 h-5 accent-[#004534]" data-testid="two-tone-toggle" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Layers className="h-4 w-4 text-[#D4AF37]" />
                        <span className="text-sm font-semibold text-[#004534]">Two-Tone Design (2025 Trend)</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {roomIsKitchen ? 'Different finish for upper & lower cabinets' : 'Contrast finish for main doors & accent trim'}
                      </p>
                    </div>
                  </label>
                </div>

                {/* Compare Mode */}
                <div>
                  <label className="block text-sm font-semibold text-[#004534] mb-2">Visualization Mode</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setCompareMode(false)} className={`px-4 py-3 rounded-lg text-sm font-medium ${!compareMode ? 'bg-[#004534] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#004534]'}`} data-testid="mode-single">Single Finish</button>
                    <button onClick={() => { setCompareMode(true); if (selectedFinishB === selectedFinish) setSelectedFinishB(FINISHES.find((f) => f.id !== selectedFinish).id); }} className={`px-4 py-3 rounded-lg text-sm font-medium ${compareMode ? 'bg-[#004534] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#004534]'}`} data-testid="mode-compare">Compare Two Finishes</button>
                  </div>
                </div>

                {/* Finish A */}
                <div>
                  <label className="text-sm font-semibold text-[#004534] mb-2 flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    {compareMode ? 'Finish A' : (twoTone ? (roomIsKitchen ? 'Upper Cabinet Finish' : 'Main Door Finish') : 'Finish / Color')}
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {FINISHES.map((f) => (
                      <button
                        key={f.id}
                        onClick={() => { if (compareMode && f.id === selectedFinishB) setSelectedFinishB(selectedFinish); setSelectedFinish(f.id); }}
                        className={`relative p-1 rounded-lg transition-all ${selectedFinish === f.id ? 'ring-2 ring-[#004534] ring-offset-2' : 'hover:ring-2 hover:ring-gray-200'}`}
                        title={`${f.label} · ${f.code}`}
                        data-testid={`finish-${f.id}`}
                      >
                        <div className={`aspect-square rounded-md ${f.border ? 'border border-gray-200' : ''}`} style={{ backgroundColor: f.color }} />
                        <span className="block text-[10px] text-center mt-1 text-gray-600 truncate">{f.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Finish B or Lower */}
                {compareMode ? (
                  <div>
                    <label className="text-sm font-semibold text-[#004534] mb-2 flex items-center gap-2">
                      <Palette className="h-4 w-4 text-[#D4AF37]" /> Finish B
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {FINISHES.map((f) => (
                        <button
                          key={f.id}
                          onClick={() => setSelectedFinishB(f.id)}
                          disabled={f.id === selectedFinish}
                          className={`relative p-1 rounded-lg transition-all ${
                            selectedFinishB === f.id ? 'ring-2 ring-[#D4AF37] ring-offset-2'
                              : f.id === selectedFinish ? 'opacity-30 cursor-not-allowed'
                                : 'hover:ring-2 hover:ring-gray-200'
                          }`}
                          title={f.label}
                          data-testid={`finish-b-${f.id}`}
                        >
                          <div className={`aspect-square rounded-md ${f.border ? 'border border-gray-200' : ''}`} style={{ backgroundColor: f.color }} />
                          <span className="block text-[10px] text-center mt-1 text-gray-600 truncate">{f.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : twoTone ? (
                  <div>
                    <label className="text-sm font-semibold text-[#004534] mb-2 flex items-center gap-2">
                      <Palette className="h-4 w-4 text-[#D4AF37]" />
                      {roomIsKitchen ? 'Lower Cabinet Finish' : 'Accent / Trim Finish'}
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {FINISHES.map((f) => (
                        <button
                          key={f.id}
                          onClick={() => setSelectedFinishLower(f.id)}
                          className={`relative p-1 rounded-lg transition-all ${selectedFinishLower === f.id ? 'ring-2 ring-[#D4AF37] ring-offset-2' : 'hover:ring-2 hover:ring-gray-200'}`}
                          title={f.label}
                          data-testid={`finish-lower-${f.id}`}
                        >
                          <div className={`aspect-square rounded-md ${f.border ? 'border border-gray-200' : ''}`} style={{ backgroundColor: f.color }} />
                          <span className="block text-[10px] text-center mt-1 text-gray-600 truncate">{f.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* Texture */}
                <div>
                  <label className="text-sm font-semibold text-[#004534] mb-2 flex items-center gap-2">
                    <Layers className="h-4 w-4" /> Surface Texture
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {TEXTURES.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setSelectedTexture(t.id)}
                        className={`text-left px-3 py-2 rounded-lg border transition-all ${selectedTexture === t.id ? 'border-[#004534] bg-[#004534]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                        data-testid={`texture-${t.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-[#004534]">{t.label}</span>
                          <span className="text-[9px] px-1.5 py-0.5 bg-[#D4AF37]/25 text-[#7A6621] rounded font-bold">{t.badge}</span>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-0.5">{t.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Kitchen-only handle */}
                {roomIsKitchen && (
                  <>
                    <div>
                      <label className="text-sm font-semibold text-[#004534] mb-2 block">Kitchen Handle Profile</label>
                      <div className="grid grid-cols-3 gap-2">
                        {HANDLE_PROFILES.map((h) => (
                          <button
                            key={h.id}
                            onClick={() => setHandleProfile(h.id)}
                            className={`text-left px-3 py-2 rounded-lg border transition-all ${handleProfile === h.id ? 'border-[#004534] bg-[#004534]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                            data-testid={`handle-profile-${h.id}`}
                          >
                            <div className="text-xs font-semibold text-[#004534]">{h.label}</div>
                            <p className="text-[10px] text-gray-500">{h.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    {(handleProfile === 'handle-based' || handleProfile === 'gola') && (
                      <div>
                        <label className="text-sm font-semibold text-[#004534] mb-2 block">Handle Colour</label>
                        <div className="grid grid-cols-4 gap-2">
                          {HANDLE_COLORS.map((h) => (
                            <button
                              key={h.id}
                              onClick={() => setHandleColor(h.id)}
                              className={`relative p-1 rounded-lg transition-all ${handleColor === h.id ? 'ring-2 ring-[#004534] ring-offset-2' : 'hover:ring-2 hover:ring-gray-200'}`}
                              title={h.label}
                              data-testid={`handle-color-${h.id}`}
                            >
                              <div className="aspect-square rounded-md border border-gray-100" style={{ backgroundColor: h.hex }} />
                              <span className="block text-[10px] text-center mt-1 text-gray-600 truncate">{h.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Wardrobe-only */}
                {roomIsWardrobe && (
                  <>
                    <div>
                      <label className="text-sm font-semibold text-[#004534] mb-2 block">Wardrobe Type</label>
                      <div className="grid grid-cols-3 gap-2">
                        {WARDROBE_TYPES.map((w) => (
                          <button
                            key={w.id}
                            onClick={() => setWardrobeType(w.id)}
                            className={`text-left px-3 py-2 rounded-lg border transition-all ${wardrobeType === w.id ? 'border-[#004534] bg-[#004534]/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                            data-testid={`wardrobe-type-${w.id}`}
                          >
                            <div className="text-xs font-semibold text-[#004534]">{w.label}</div>
                            <p className="text-[10px] text-gray-500">{w.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-[#004534] mb-2 block">Door Design</label>
                      <div className="grid grid-cols-3 gap-2">
                        {WARDROBE_DOOR_DESIGNS.map((d) => (
                          <button
                            key={d.id}
                            onClick={() => setWardrobeDoorDesign(d.id)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${wardrobeDoorDesign === d.id ? 'border-[#004534] bg-[#004534]/5 text-[#004534]' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                            data-testid={`wardrobe-door-${d.id}`}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Style */}
                <div>
                  <label className="text-sm font-semibold text-[#004534] mb-2 block">Design Style</label>
                  <div className="grid grid-cols-2 gap-2">
                    {STYLES.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setSelectedStyle(s.id)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${selectedStyle === s.id ? 'bg-[#004534] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#004534]'}`}
                        data-testid={`style-${s.id}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Lighting */}
                <div>
                  <label className="text-sm font-semibold text-[#004534] mb-2 flex items-center gap-2">
                    <Sun className="h-4 w-4 text-[#D4AF37]" /> Ambient Lighting
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {LIGHTINGS.map((l) => (
                      <button
                        key={l.id}
                        onClick={() => setLighting(l.id)}
                        className={`px-3 py-2 rounded-lg text-xs font-medium ${lighting === l.id ? 'bg-[#004534] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#004534]'}`}
                        data-testid={`lighting-${l.id}`}
                      >
                        <span className="mr-1">{l.icon}</span>{l.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Error */}
                {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}

                {/* Generate */}
                <button
                  onClick={generateVisualization}
                  disabled={isGenerating}
                  className="w-full py-4 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-[#004534] hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="generate-btn"
                >
                  {isGenerating ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> {compareMode ? 'Generating Both Finishes…' : 'Generating (30-60s)…'}</>
                  ) : (
                    <><Wand2 className="h-5 w-5" /> {compareMode ? 'Generate Comparison' : 'Generate My Design'}</>
                  )}
                </button>

                {isGenerating && (
                  <div className="p-4 bg-[#004534]/5 border border-[#004534]/10 rounded-lg text-center" data-testid="visualizer-progress">
                    <p className="text-sm font-medium text-[#004534] animate-pulse">{loadingMessages[loadingMsgIndex]}</p>
                    <p className="text-xs text-gray-500 mt-1">{compareMode ? 'Both finishes take 60–90s.' : 'Usually 30–60s.'} Please keep this tab open.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 3 - Comparison */}
          {step === 3 && comparisonResult && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto" data-testid="comparison-result">
              <h2 className="text-2xl lg:text-3xl font-bold text-[#004534] text-center mb-2 font-['Outfit']">Side-by-Side Comparison</h2>
              <p className="text-gray-500 text-center mb-8">Same room, two CriLam finishes — which one speaks to you?</p>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {[{ key: 'a', label: 'Option A', accent: 'bg-[#004534] text-white' }, { key: 'b', label: 'Option B', accent: 'bg-[#D4AF37] text-[#004534]' }].map((opt) => (
                  <div key={opt.key} className="bg-white rounded-xl overflow-hidden shadow-lg">
                    <div className="relative aspect-video bg-gray-100">
                      <img src={comparisonResult[opt.key].url} alt={`${opt.label} - ${FINISHES.find((f) => f.id === comparisonResult[opt.key].finish)?.label}`} className="w-full h-full object-cover" />
                      <div className={`absolute bottom-4 left-4 px-3 py-1 text-sm rounded-full font-medium ${opt.accent}`}>
                        {opt.label}: {FINISHES.find((f) => f.id === comparisonResult[opt.key].finish)?.label}
                      </div>
                    </div>
                    <div className="p-4 flex justify-center">
                      <button onClick={() => downloadCompareImage(opt.key)} className="flex items-center gap-2 text-sm font-medium text-[#004534] hover:text-[#D4AF37]" data-testid={`download-option-${opt.key}-btn`}>
                        <Download className="h-4 w-4" /> Download {opt.label}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 justify-center">
                <button onClick={shareComparisonToWhatsApp} className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-lg hover:bg-[#128C7E]" data-testid="whatsapp-compare-btn">
                  <MessageCircle className="h-5 w-5" /> Ask Friends on WhatsApp
                </button>
                <button onClick={() => setStep(2)} className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-600 rounded-lg hover:border-[#004534] hover:text-[#004534]">
                  <RefreshCw className="h-5 w-5" /> Try Different Finishes
                </button>
                <button onClick={resetVisualizer} className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-600 rounded-lg hover:border-[#004534] hover:text-[#004534]">
                  <ImageIcon className="h-5 w-5" /> New Photo
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3 - Single Result */}
          {step === 3 && !comparisonResult && generatedImage && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-5xl mx-auto">
              <h2 className="text-2xl lg:text-3xl font-bold text-[#004534] text-center mb-2 font-['Outfit']">Your CriLam Design</h2>
              <p className="text-gray-500 text-center mb-6">
                {FINISHES.find((f) => f.id === selectedFinish)?.label} · {TEXTURES.find((t) => t.id === selectedTexture)?.label} · {LIGHTINGS.find((l) => l.id === lighting)?.label}
              </p>

              {/* Before/After slider */}
              {previewUrl && showBeforeAfter && !selectedPreset ? (
                <BeforeAfterSlider beforeSrc={previewUrl} afterSrc={generatedImage} />
              ) : (
                <div className="relative rounded-xl overflow-hidden shadow-lg bg-gray-100">
                  <img src={generatedImage} alt="CriLam Visualization" className="w-full h-auto" />
                  <div className="absolute top-4 left-4 px-3 py-1 bg-[#D4AF37] text-[#004534] text-xs font-bold rounded-full">CriLam AI Design Studio</div>
                </div>
              )}

              {previewUrl && !selectedPreset && (
                <div className="mt-3 flex justify-center">
                  <button
                    onClick={() => setShowBeforeAfter((s) => !s)}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-[#004534] text-[#004534] rounded-lg hover:bg-[#004534] hover:text-white"
                    data-testid="toggle-before-after"
                  >
                    <GitCompare className="h-4 w-4" />
                    {showBeforeAfter ? 'Show Full Result' : 'Show Before / After Slider'}
                  </button>
                </div>
              )}

              {/* Spec panel */}
              <div className="mt-6 bg-white rounded-xl p-6 border border-gray-200 grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-bold text-[#004534] mb-3">Product Specifications</h4>
                  <div className="space-y-1.5 text-sm text-gray-700">
                    <div className="flex justify-between"><span className="text-gray-500">Product</span><span className="font-medium">CriLam {roomIsKitchen ? 'Modular Kitchen' : 'Wardrobe'} · 6H Acrylic</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">{twoTone ? 'Upper Finish' : 'Finish'}</span><span className="font-medium">{FINISHES.find((f) => f.id === selectedFinish)?.label} · {FINISHES.find((f) => f.id === selectedFinish)?.code}</span></div>
                    {twoTone && (<div className="flex justify-between"><span className="text-gray-500">{roomIsKitchen ? 'Lower Finish' : 'Accent Finish'}</span><span className="font-medium">{FINISHES.find((f) => f.id === selectedFinishLower)?.label} · {FINISHES.find((f) => f.id === selectedFinishLower)?.code}</span></div>)}
                    <div className="flex justify-between"><span className="text-gray-500">Texture</span><span className="font-medium">{TEXTURES.find((t) => t.id === selectedTexture)?.label}</span></div>
                    {roomIsKitchen && (<div className="flex justify-between"><span className="text-gray-500">Handle</span><span className="font-medium">{HANDLE_PROFILES.find((h) => h.id === handleProfile)?.label}</span></div>)}
                    {roomIsWardrobe && (<>
                      <div className="flex justify-between"><span className="text-gray-500">Type</span><span className="font-medium">{WARDROBE_TYPES.find((w) => w.id === wardrobeType)?.label}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Door Design</span><span className="font-medium">{WARDROBE_DOOR_DESIGNS.find((d) => d.id === wardrobeDoorDesign)?.label}</span></div>
                    </>)}
                    <div className="flex justify-between"><span className="text-gray-500">Style</span><span className="font-medium">{STYLES.find((s) => s.id === selectedStyle)?.label}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Lighting</span><span className="font-medium">{LIGHTINGS.find((l) => l.id === lighting)?.label}</span></div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[#004534] mb-3">CriLam PUR-Bond™ Technology Guarantees</h4>
                  <div className="flex flex-wrap gap-2">
                    {['✓ 6H Hardness', '✓ Heat Resistant 150°C', '✓ 100% Waterproof', '✓ 10-Year Written Guarantee', '✓ Zero Bubble · Sealed Edges'].map((b) => (
                      <span key={b} className="px-3 py-1 bg-[#004534]/8 text-[#004534] text-xs font-semibold rounded-full">{b}</span>
                    ))}
                  </div>
                  <div className="mt-4 flex items-start gap-2 text-xs text-gray-600 bg-[#D4AF37]/10 rounded-lg p-3">
                    <Info className="h-3.5 w-3.5 text-[#7A6621] mt-0.5 flex-shrink-0" />
                    <p>Made at Kanpur&apos;s first CriLam PUR-Bond™ lamination facility · Uttar Pradesh.</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex flex-wrap gap-3 justify-center">
                <button onClick={shareToWhatsApp} className="flex items-center gap-2 px-5 py-3 bg-[#25D366] text-white rounded-lg hover:bg-[#128C7E]" data-testid="whatsapp-share-btn">
                  <MessageCircle className="h-5 w-5" /> Share on WhatsApp
                </button>
                <button onClick={downloadImage} className="flex items-center gap-2 px-5 py-3 bg-[#004534] text-white rounded-lg hover:bg-[#D4AF37] hover:text-[#004534]" data-testid="download-btn">
                  <Download className="h-5 w-5" /> Download Image
                </button>
                <button onClick={downloadPDF} disabled={pdfGenerating} className="flex items-center gap-2 px-5 py-3 border-2 border-[#004534] text-[#004534] rounded-lg hover:bg-[#004534] hover:text-white disabled:opacity-60" data-testid="pdf-btn">
                  {pdfGenerating ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileText className="h-5 w-5" />}
                  Design Sheet (PDF)
                </button>
                <button onClick={() => setArActive(true)} className="flex items-center gap-2 px-5 py-3 border-2 border-[#D4AF37] text-[#004534] rounded-lg hover:bg-[#D4AF37]" data-testid="ar-btn">
                  <Camera className="h-5 w-5" /> Try in My Room (AR)
                </button>
                <KenBurnsExport
                  imageUrl={generatedImage}
                  brandLabel={`${roomIsKitchen ? 'Kitchen' : 'Wardrobe'} · Nail-Free`}
                  filename={`crilam-${selectedRoom}-${selectedFinish}-walkthrough`}
                />
                <button onClick={() => setStep(2)} className="flex items-center gap-2 px-5 py-3 border-2 border-gray-300 text-gray-600 rounded-lg hover:border-[#004534] hover:text-[#004534]">
                  <RefreshCw className="h-5 w-5" /> Try Different Style
                </button>
                <button onClick={resetVisualizer} className="flex items-center gap-2 px-5 py-3 border-2 border-gray-300 text-gray-600 rounded-lg hover:border-[#004534] hover:text-[#004534]">
                  <ImageIcon className="h-5 w-5" /> New Photo
                </button>
              </div>

              <div className="mt-6 flex justify-center">
                <Link to="/contact" state={{ prefill: { subject: `${roomIsKitchen ? 'Kitchen' : 'Wardrobe'} Quote`, product: roomIsKitchen ? 'Modular Kitchen' : 'Wardrobe', message: `Interested in a CriLam ${roomIsKitchen ? 'kitchen' : 'wardrobe'} in ${FINISHES.find((f) => f.id === selectedFinish)?.label} ${TEXTURES.find((t) => t.id === selectedTexture)?.label} finish.` } }}>
                  <button className="px-6 py-3 bg-[#004534] text-white rounded-lg hover:bg-[#D4AF37] hover:text-[#004534] font-bold flex items-center gap-2">
                    Get Exact Quote <ArrowRight className="h-4 w-4" />
                  </button>
                </Link>
              </div>
            </motion.div>
          )}

        </div>
      </section>

      <AnimatePresence>
        {arActive && generatedImage && (
          <ARDoorPreview overlayImageUrl={generatedImage} onClose={() => setArActive(false)} brandLabel={`CriLam ${roomIsKitchen ? 'Kitchen' : 'Wardrobe'} · Try In Your Room`} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIVisualizer;
